import React from 'react';

import SimpleSearchInput from '@common/react/components/Forms/SimpleSearchInput/SimpleSearchInput';
import { BaseParams } from '@common/react/objects/BaseParams';
import { WithId } from '@common/typescript/objects/WithId';
import SimpleLoader from '@common/react/components/UI/Loaders/SimpleLoader';
import { ItemsProvider, PaginationOptions } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import LoadMoreButton from '@common/react/components/Core/ItemsProvider/LoadMoreButton';
import Loader from '@common/react/components/Core/LoaderProvider/Loader';

import '@common/react/scss/components/searchWithCheckboxes.scss';

interface Props<T extends WithId> {
	type: string;
	onChange: (values: Array<number>) => void;
	loadRequest?: string;
	values?: Array<number>;
	initItems?: Array<T>;
	className?: string;
	additionalParams?: BaseParams;
	renderName?: (item: T) => string;
	pagination?: PaginationOptions;
	title?: string;
	titleClassName?: string;
	showLoadMore?: boolean;
	loadMorePageSize?: number;
	initLoadedAll?: boolean;
}

const SearchWithCheckboxes: <T extends WithId>(props: Props<T>) => React.ReactElement = <T extends WithId, >(props) => {
	const {
		type,
		className,
		initItems,
		additionalParams,
		values = [],
		renderName = (item) => item.name,
		onChange,
		loadRequest,
		pagination = { pageSize: 50, current: 1 },
		title = '',
		titleClassName = '',
		showLoadMore = true,
		loadMorePageSize = 200,
		initLoadedAll = false,
	} = props;
	const [selectedIds, setSelectedIds] = React.useState(() => {
		const obj = {};
		values?.forEach((id) => {
			obj[id] = 1;
		});
		return obj;
	});
	const [loadAll, setLoadAll] = React.useState(initLoadedAll);
	const [search, setSearch] = React.useState('');
	const handleCheck = (e) => {
		const id = e.target.name;
		if (selectedIds[id]) {
			delete selectedIds[id];
		} else {
			selectedIds[id] = +id;
		}
		setSelectedIds({ ...selectedIds });
		onChange(Object.keys(selectedIds).map((item) => +item));
	};

	React.useEffect(() => {
		if (`${values?.map((el) => +el).sort()}` !== `${Object.keys(selectedIds).map((item) => +item).sort()}`) {
			const obj = {};
			(values || []).forEach((item) => {
				obj[item] = 1;
			});
			setSelectedIds(obj);
		}
	}, [`${values}`]);
	const hasSelected = React.useMemo(() => {
		return !!Object.keys(selectedIds).length;
	}, [selectedIds]);
	const id = `${title}${type}checkboxes-search`;

	return <div className={`search-with-checkboxes ${className}`}>
		<div className="search-with-checkboxes-title">
			<label className={titleClassName} htmlFor={id}>
				{title}
			</label>
			<button
				type="button"
				disabled={!hasSelected}
				className={`btn btn-sm search-with-checkboxes-clear ${hasSelected ? '' : 'search-with-checkboxes-clear-disabled'}`}
				onClick={() => {
					setSelectedIds({});
					onChange([]);
				}}
			>
				Clear
			</button>
		</div>
		<ItemsProvider
			pagination={pagination}
			type={type}
			loadRequest={loadRequest}
			items={initItems}
			skipInitLoad={!!initItems}
			filters={{
				...additionalParams,
			}}
			onLoad={(res) => {
				if (res.count <= pagination.pageSize) {
					setLoadAll(true);
				}
			}}
		>
			{({ state: { items, loading, filters }, actions: { handleChange } }) => {
				const resItems = items
					.filter((item) => {
						return renderName(item).toLowerCase().includes(search.toLowerCase());
					});
				return <>
					<div className="is-relative">
						<SimpleSearchInput
							value={filters.text}
							onChange={(e) => {
								setSearch(e.currentTarget.value as string || '');
								!loadAll && !loading && handleChange({ pageSize: loadMorePageSize, current: 1 })
									.then(() => {
										setLoadAll(true);
									});
							}}
							withoutForm
							otherInputProps={{
								id: `${title}${type}checkboxes-search`,
							}}
						/>
						{loading ? <Loader defaultLoader={<SimpleLoader />} type="autocompleteLoader" /> : null}
					</div>
					<ul className="checkbox-list">
						{resItems
							.map((item) => <li key={item.id}>
								<label className={`checkbox-list__item ${selectedIds[item.id] ? 'checkbox-list__item__checked' : ''}`}>
									<span className="checkbox-list__item-control">
										<span className="checkbox-list__item-inner" />
										<input
											name={item.id.toString()}
											type="checkbox"
											checked={!!selectedIds[item.id]}
											onChange={handleCheck}
										/>
									</span>
									<span className="checkbox-list__item-name">
										{renderName(item)}
									</span>
								</label>
							</li>)}
						{resItems.length === 0 && !loading && loadAll
							? <li key="empty" className="text-center" style={{ padding: '5px 0' }}>
								<strong>
									Empty
								</strong>
							</li> : null}
						{showLoadMore && !loadAll && <li key="load-more" className="text-center">
							<LoadMoreButton
								pageSize={loadMorePageSize}
								afterLoad={() => {
									setLoadAll(true);
								}}
							>
								Load More
							</LoadMoreButton>
						</li>}
					</ul>
				</>;
			}}
		</ItemsProvider>
	</div>;
};

export default SearchWithCheckboxes;
